import { Alert, Paragraph, Textfield } from '@digdir/designsystemet-react';
import { FC, useEffect, useMemo, useRef } from 'react';
import { LatLngBoundsExpression, Marker as LeafletMarker, icon } from 'leaflet';
import { useTranslation } from 'react-i18next';
import { AttributionControl, MapContainer, Marker, TileLayer } from 'react-leaflet';

import styled from '../../../../../../styled';
import { GeoLocationStatus } from '../../hooks/useGeoLocation';
import { hasValidCoordinates } from '../../utils/coordinates';
import { NORWAY_BBOX, isInsideNorwayBoundary } from '../../utils/geoValidation';

import 'leaflet/dist/leaflet.css';

const NORWAY_MAX_BOUNDS: LatLngBoundsExpression = [
  [NORWAY_BBOX.minLat, NORWAY_BBOX.minLon],
  [NORWAY_BBOX.maxLat, NORWAY_BBOX.maxLon]
];

const MAP_MIN_ZOOM = 4;

const MapWrapper = styled.div`
  width: 100%;
  min-height: 320px;
  border-radius: var(--ds-border-radius-md);
  overflow: hidden;
`;

const CoordinatesRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--ds-size-4);

  > * {
    flex: 1;
  }
`;

const mapMarkerIcon = icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

const StatusMessage: FC<{ status: GeoLocationStatus; hitCount?: number }> = ({ status, hitCount }) => {
  const { t } = useTranslation();

  if (status === 'idle') {
    return null;
  }

  if (status === 'found') {
    return (
      <Alert data-color="info">
        <Paragraph>{t('register.solar.form.location.status.found')}</Paragraph>
      </Alert>
    );
  }

  if (status === 'multiple') {
    return (
      <Alert data-color="warning">
        <Paragraph>
          {t('register.solar.form.location.status.multiple', { hitCount: hitCount ?? 0 })}
        </Paragraph>
      </Alert>
    );
  }

  if (status === 'not-found') {
    return (
      <Alert data-color="warning">
        <Paragraph>
          {t('register.solar.form.location.status.notFound')}
        </Paragraph>
      </Alert>
    );
  }

  if (status === 'error') {
    return (
      <Alert data-color="danger">
        <Paragraph>{t('register.solar.form.location.status.error')}</Paragraph>
      </Alert>
    );
  }

  return (
    <Alert data-color="info">
      <Paragraph>{t('register.solar.form.location.status.loading')}</Paragraph>
    </Alert>
  );
};

type Props = {
  latitude?: number;
  longitude?: number;
  status: GeoLocationStatus;
  hitCount?: number;
  onCoordinateChange: (coordinates: Coordinate) => void;
};

type Coordinate = {
  latitude: number;
  longitude: number;
};

export const LocationMap: FC<Props> = ({ latitude, longitude, status, hitCount, onCoordinateChange }) => {
  const { t } = useTranslation();
  const center = useMemo((): Coordinate | undefined => {
    if (!hasValidCoordinates(latitude, longitude)) {
      return undefined;
    }

    return { latitude: latitude as number, longitude: longitude as number };
  }, [latitude, longitude]);

  const lastValidPositionRef = useRef<Coordinate | undefined>(center);

  useEffect(() => {
    if (center && isInsideNorwayBoundary(center.latitude, center.longitude)) {
      lastValidPositionRef.current = center;
    }
  }, [center]);

  return (
    <>
      <StatusMessage status={status} hitCount={hitCount} />
      {center && (
        <Paragraph data-size="sm" style={{ marginBottom: 'var(--ds-size-3)' }}>
          {t('register.solar.form.location.markerHint')}
        </Paragraph>
      )}
      {center && (
        <MapWrapper>
          <MapContainer
            center={[center.latitude, center.longitude]}
            zoom={17}
            minZoom={MAP_MIN_ZOOM}
            maxBounds={NORWAY_MAX_BOUNDS}
            maxBoundsViscosity={1.0}
            style={{ height: '360px', width: '100%' }}
            attributionControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <AttributionControl prefix={false} position="bottomright" />
            <Marker
              icon={mapMarkerIcon}
              position={[center.latitude, center.longitude]}
              draggable
              eventHandlers={{
                drag: (event) => {
                  const marker = event.target as LeafletMarker;
                  const { lat, lng } = marker.getLatLng();
                  if (isInsideNorwayBoundary(lat, lng)) {
                    lastValidPositionRef.current = { latitude: lat, longitude: lng };
                    return;
                  }
                  const fallback = lastValidPositionRef.current;
                  if (fallback) {
                    marker.setLatLng([fallback.latitude, fallback.longitude]);
                  }
                },
                dragend: (event) => {
                  const marker = event.target as LeafletMarker;
                  const { lat, lng } = marker.getLatLng();
                  if (isInsideNorwayBoundary(lat, lng)) {
                    lastValidPositionRef.current = { latitude: lat, longitude: lng };
                    onCoordinateChange({ latitude: lat, longitude: lng });
                    return;
                  }
                  const fallback = lastValidPositionRef.current;
                  if (fallback) {
                    marker.setLatLng([fallback.latitude, fallback.longitude]);
                  }
                }
              }}
            />
          </MapContainer>
        </MapWrapper>
      )}
      {center && (
        <CoordinatesRow>
          <Textfield
            label={t('register.solar.form.location.latitude')}
            value={center.latitude.toFixed(6)}
            readOnly
          />
          <Textfield
            label={t('register.solar.form.location.longitude')}
            value={center.longitude.toFixed(6)}
            readOnly
          />
        </CoordinatesRow>
      )}
    </>
  );
};
